import React, { useState, useEffect, useRef } from "react";
import { Formik, Form } from "formik";
import useAuth from "../../hooks/use-auth";
import * as Yup from "yup";
import { Box, Button, Text } from "@vinomofo/components";
import { TrackingEventName } from "../../utils/event-tracking";
import { HideShowPasswordIcon } from "@vinomofo/icons";
import { TextField } from "../form-inputs/FormFields";
import { FormInput, FormBtn } from "../login-form/LoginForm";
import isBrowser from "../../utils/is-browser";

interface SignupFormProps {
  isSpirit: boolean;
  formLocation: string;
  userEmail?: string | null | undefined;
  category?: "Offer" | "Event" | "Spirit";
}

const SignupForm = React.memo(
  ({
    isSpirit = false,
    formLocation = "Paywall",
    userEmail = "",
    category = "Spirit",
  }: SignupFormProps) => {
    const eventName = `Login on ${formLocation} Clicked` as TrackingEventName;
    const {
      loginWithCredentials,
      createUserInAdmin,
      unLockedPaywall,
      login,
    } = useAuth();
    const [revealPassword, setRevealPassword] = useState(false);
    const encodedCredentialsRef = useRef<string | null>(null);
    const handleToggleClick = () => {
      setRevealPassword(!revealPassword);
    };

    useEffect(() => {
      if (unLockedPaywall && isBrowser()) {
        const autoLogin = setTimeout(() => {
          if (encodedCredentialsRef.current) {
            loginWithCredentials(
              { returnTo: window.location.pathname },
              encodedCredentialsRef.current
            );
            encodedCredentialsRef.current = null; // Clear after use
          }
          clearTimeout(autoLogin);
        }, 100);
      }
    }, [unLockedPaywall]);

    const onSubmitForm = async (
      values: { email: string; password: string },
      setSubmitting: (isSubmitting: boolean) => void,
      setError: (msg: string | null) => void
    ) => {
      setSubmitting(true);
      try {
        await createUserInAdmin({
          email: values?.email,
          password: values?.password,
        });
      } catch (error) {
        console.error("Failed to connect to the server.");
        setError("Server error. Please try again later.");
      } finally {
        const delimiter = "A8k@b#Z2!x";
        const timecode = Date.now(); // UTC timestamp in milliseconds
        const combined = `${values?.email}${delimiter}${values?.password}${delimiter}${timecode}`;
        const encoded = btoa(combined);
        encodedCredentialsRef.current = encoded;

        setSubmitting(false);
      }
    };

    const validationSchema = Yup.object({
      email: Yup.string()
        .trim()
        .nullable()
        .email("Invalid email")
        .required("Required"),
      password: Yup.string()
        .required("Required")
        .min(8, "Password must be at least 8 characters"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("password"), null], "Passwords must match")
        .required("Please confirm your password"),
    });

    return (
      <Formik
        enableReinitialize
        validateOnMount={true}
        initialValues={{ email: userEmail, password: "" }}
        validationSchema={validationSchema}
        onSubmit={(values, { setSubmitting, setFieldError }) => {
          onSubmitForm(values, setSubmitting, (msg) => {
            if (msg) setFieldError("general", msg);
          });
        }}
      >
        {({ isSubmitting, errors, values, isValid }) => (
          <Form style={{ width: "100%" }}>
            <Box
              display="flex"
              justifyContent="stretch"
              flexDirection={["column", "column", "column"]}
              gridGap={0}
            >
              <Box position={"relative"} width="100%">
                <FormInput
                  type="email"
                  name="email"
                  placeholder="Your email address"
                  component={TextField}
                  className={"bottom-border"}
                  required
                  isSpirit={isSpirit}
                />
              </Box>
              <Box position={"relative"} width="100%">
                <FormInput
                  type={revealPassword ? "text" : "password"}
                  name="password"
                  placeholder="Create password"
                  component={TextField}
                  className={"bottom-border"}
                  required
                  isSpirit={isSpirit}
                />
                <Box position={"absolute"} right={10} top={10}>
                  <Button
                    tabIndex="-1"
                    type="button"
                    onClick={handleToggleClick}
                    hoverColor="primary"
                    color={revealPassword ? "primary" : "dorianGray"}
                  >
                    <HideShowPasswordIcon
                      isClicked={revealPassword}
                      width="24"
                      height="24"
                    />
                  </Button>
                </Box>
              </Box>
              <Box position={"relative"} width="100%">
                <FormInput
                  type={revealPassword ? "text" : "password"}
                  name="confirmPassword"
                  placeholder="Re-enter password"
                  component={TextField}
                  className={"bottom-border"}
                  required
                  isSpirit={isSpirit}
                />
                <Box position={"absolute"} right={10} top={10}>
                  <Button
                    tabIndex="-1"
                    type="button"
                    onClick={handleToggleClick}
                    hoverColor="primary"
                    color={revealPassword ? "primary" : "dorianGray"}
                  >
                    <HideShowPasswordIcon
                      isClicked={revealPassword}
                      width="24"
                      height="24"
                    />
                  </Button>
                </Box>
              </Box>
            </Box>
            {errors?.general && (
              <Box>
                <Text color={"red"} fontSize={2}>
                  {errors?.general}
                </Text>
              </Box>
            )}
            <Box
              display="flex"
              justifyContent="stretch"
              flexDirection={["column", "column", "column"]}
              gridGap={0}
              textAlign="center"
              alignItems="center"
              mt="8px"
              mb="20px"
              fontFamily={`"Graphik", sans-serif`}
            >
              <FormBtn
                type="submit"
                disabled={isSubmitting || !isValid}
                p="10px 30px"
                fontSize="18px"
                fontWeight={500}
                variant="primary"
                width="100%"
                lineHeight="1.2"
                borderWidth={"2px"}
                borderStyle={"solid"}
                isSpirit={isSpirit}
              >
                {isSubmitting
                  ? "Processing..."
                  : category === "Spirit"
                  ? "Continue to Whiskymofo"
                  : `Unlocked ${category}`}
              </FormBtn>
            </Box>
          </Form>
        )}
      </Formik>
    );
  }
);

export default SignupForm;
